import { db } from '@/firebase/config';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
} from 'firebase/firestore';
import { hashPassword } from './hash';

/**
 * Sign up a new user
 * Throws error if username already exists (case-insensitive)
 */
export async function signupUser(
  name: string,
  email: string | undefined,
  password: string
) {
  if (!name.trim()) throw new Error('Username cannot be empty');

  const normalizedName = name.trim().toLowerCase();
  const usersRef = collection(db, 'users');

  // Check duplicate usernames (case-insensitive)
  const q = query(usersRef, where('normalizedName', '==', normalizedName));
  const snap = await getDocs(q);
  if (!snap.empty) {
    throw new Error('User already exists');
  }

  const passwordHash = await hashPassword(password);

  const docRef = await addDoc(usersRef, {
    name: name.trim(),
    normalizedName,       // store lowercase version for uniqueness check
    email: email?.trim() || null,
    passwordHash,
    createdAt: Date.now(),
  });

  return { id: docRef.id, name: name.trim(), email: email?.trim() || null };
}

/**
 * Login existing user
 */
export async function loginUser(name: string, password: string) {
  const normalizedName = name.trim().toLowerCase();
  const usersRef = collection(db, 'users');

  const q = query(usersRef, where('normalizedName', '==', normalizedName));
  const snap = await getDocs(q);

  if (snap.empty) {
    throw new Error('User not found');
  }

  const user = snap.docs[0].data();
  const passwordHash = await hashPassword(password);

  if (user.passwordHash !== passwordHash) {
    throw new Error('Invalid password');
  }

  return {
    id: snap.docs[0].id,
    name: user.name,
    email: user.email,
  };
}
